/*
 * Kopi Blok — Product page: keep the price in front of the customer.
 * Enhances the `kb-figma-product-main` section.
 *
 * Everything here mirrors numbers the section already maintains — the variant price in
 * #kbf-prod-price, and the add-on running total in [data-kbf-totals] — so no price is
 * ever calculated twice and the two can't disagree.
 *
 *   1. Inline price — a row pinned to the top of the option pickers, so changing size
 *      shows the new price without scrolling back up. All screen sizes.
 *   2. Summary card — product price + add-ons + total. Under the gallery on mobile,
 *      directly above the add-to-cart button on desktop. Replaces the section's own
 *      totals box (hidden via the .kbf-has-summary class).
 *   3. Thumbnail strip — keeps the active thumbnail in view now that it scrolls.
 *   4. Sticky bar — every screen size. Title + live total + a mirror of the ATC button,
 *      so the price the customer will pay stays on screen while they browse photos and
 *      pick options. Hides itself once the real ATC button is in view.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Only run on product pages
  if (!/^\/products\//.test(window.location.pathname)) return;

  var MOBILE_MAX = 750;

  // Same breakpoint the stylesheet below uses, asked the same way, so the two can't disagree.
  function isMobile() {
    if (window.matchMedia) return window.matchMedia('(max-width: ' + (MOBILE_MAX - 1) + 'px)').matches;
    return window.innerWidth > 0 && window.innerWidth < MOBILE_MAX;
  }

  // --- The one price the page treats as current. The section rewrites its text on every
  //     variant change, so everything else can just follow it. ---
  function findPriceEl() {
    return (
      document.getElementById('kbf-prod-price') ||
      document.querySelector('.kbf-prod__price') ||
      document.querySelector('.price__sale .price-item--sale') ||
      document.querySelector('.price__regular .price-item--regular') ||
      document.querySelector('.price-item--regular, .price-item, [data-product-price]')
    );
  }

  function text(el) {
    return el ? (el.textContent || '').trim().replace(/\s+/g, ' ') : '';
  }

  function findATC() {
    return (
      document.querySelector('.kbf-prod__atc') ||
      document.querySelector('product-form button[name="add"]:not([disabled])') ||
      document.querySelector('form[action*="/cart/add"] button[name="add"]') ||
      document.querySelector('form[action*="/cart/add"] [type="submit"]')
    );
  }

  function atcLabel(btn) {
    if (!btn) return 'הוסיפו לסל';
    var span = btn.querySelector('span');
    return ((span ? span.textContent : btn.textContent) || 'הוסיפו לסל').trim() || 'הוסיפו לסל';
  }

  function getProductTitle() {
    var t =
      document.querySelector('[data-kbf-product-title]') ||
      document.querySelector('.kbf-prod__title') ||
      document.querySelector('.product__title h1, h1.product__title, h1');
    return t ? (t.textContent || '').trim().slice(0, 60) : '';
  }

  function injectStyles() {
    if (document.getElementById('kbf-sticky-atc-styles')) return;
    var style = document.createElement('style');
    style.id = 'kbf-sticky-atc-styles';
    style.textContent = [
      /* --- Inline price, sitting on top of the option pickers --- */
      '.kbf-live-price{',
        'display:flex;align-items:baseline;justify-content:space-between;gap:12px;',
        'background:var(--kbf-bg-off-white,#FDFAF8);',
        'border:1px solid var(--kbf-border,#E2D5C8);',
        'border-radius:var(--kbf-r-card,16px);padding:12px 16px;',
        'font-family:var(--kbf-font-sans,"Assistant","DM Sans",system-ui,sans-serif);',
        'direction:rtl;text-align:right;',
      '}',
      '.kbf-live-price__label{font-size:15px;color:var(--kbf-text-muted,#5C4F4F)}',
      '.kbf-live-price__value{',
        'font-size:22px;font-weight:700;color:var(--kbf-text-dark,#282020);',
        'display:inline-block;transform-origin:right center;',
      '}',

      /* --- Summary card: product price + add-ons + total --- */
      '.kbf-price-summary{',
        'display:flex;flex-direction:column;gap:8px;width:100%;box-sizing:border-box;',
        'background:var(--kbf-bg-off-white,#FDFAF8);',
        'border:1px solid var(--kbf-border,#E2D5C8);',
        'border-radius:var(--kbf-r-card,16px);padding:14px 18px;',
        'font-family:var(--kbf-font-sans,"Assistant","DM Sans",system-ui,sans-serif);',
        'direction:rtl;text-align:right;',
      '}',
      '.kbf-price-summary__row{',
        'display:flex;align-items:baseline;justify-content:space-between;gap:12px;',
        'font-size:15px;color:var(--kbf-text-muted,#5C4F4F);',
      '}',
      '.kbf-price-summary__row[hidden]{display:none}',
      '.kbf-price-summary__row--sum{',
        'font-size:20px;font-weight:700;color:var(--kbf-text-dark,#282020);',
      '}',
      '.kbf-price-summary__row--sum.has-breakdown{',
        'padding-top:8px;border-top:1px solid var(--kbf-border,#E2D5C8);',
      '}',
      '.kbf-price-summary__value{display:inline-block;transform-origin:right center}',

      /* --- Shared highlight when a number changes --- */
      '.kbf-price-flash{animation:kbf-price-flash .6s ease}',
      '@keyframes kbf-price-flash{',
        '0%{transform:scale(1)}',
        '25%{transform:scale(1.08);color:var(--kbf-coral,#C4958E)}',
        '100%{transform:scale(1)}',
      '}',
      '@media (prefers-reduced-motion:reduce){.kbf-price-flash{animation:none}}',

      /* --- Sticky bar --- */
      '.kbf-sticky-atc{',
        'position:fixed;left:0;right:0;bottom:0;z-index:90;',
        'background:rgba(253,250,248,0.97);',
        '-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);',
        'border-top:1px solid rgba(40,32,32,0.08);',
        'padding:10px 14px calc(10px + env(safe-area-inset-bottom)) 14px;',
        'display:flex;align-items:center;gap:12px;direction:rtl;',
        'box-shadow:0 -6px 20px rgba(40,32,32,0.10);',
        'transform:translateY(110%);transition:transform .28s ease;',
        'font-family:"Assistant","DM Sans",system-ui,sans-serif;',
      '}',
      '.kbf-sticky-atc.is-visible{transform:translateY(0)}',
      '.kbf-sticky-atc__info{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;text-align:right}',
      '.kbf-sticky-atc__title{font-size:13px;font-weight:600;color:#282020;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.2}',
      '.kbf-sticky-atc__price{font-size:15px;font-weight:700;color:#C4958E;line-height:1.2;display:inline-block;transform-origin:right center}',
      '.kbf-sticky-atc__btn{',
        'background:#C4958E;color:#fff;border:0;border-radius:999px;',
        'padding:13px 22px;font-size:15px;font-weight:700;',
        'cursor:pointer;flex-shrink:0;min-width:140px;',
        'transition:background .2s ease,transform .15s ease;',
        'font-family:inherit;',
      '}',
      '.kbf-sticky-atc__btn:hover{background:#5C4F4F}',
      '.kbf-sticky-atc__btn:active{transform:scale(.97)}',
      '.kbf-sticky-atc__btn[disabled]{background:#bfb6b3;cursor:not-allowed}',
      '.kbf-sticky-atc__inner{display:flex;align-items:center;gap:12px;width:100%;box-sizing:border-box}',
      '.kbf-sticky-atc__pricerow{display:flex;align-items:baseline;gap:6px;flex-wrap:wrap}',
      '.kbf-sticky-atc__note{font-size:12px;font-weight:500;color:#5C4F4F}',
      '.kbf-sticky-atc__note[hidden]{display:none}',
      /* Desktop: same bar, sized for the wider layout and aligned to the page container */
      '@media (min-width:' + MOBILE_MAX + 'px){',
        '.kbf-sticky-atc{padding:12px 4% calc(12px + env(safe-area-inset-bottom)) 4%}',
        '.kbf-sticky-atc__inner{max-width:1512px;margin:0 auto}',
        '.kbf-sticky-atc__title{font-size:15px}',
        '.kbf-sticky-atc__price{font-size:19px}',
        '.kbf-sticky-atc__btn{min-width:220px;padding:14px 28px;font-size:16px}',
      '}'
    ].join('');
    document.head.appendChild(style);
  }

  // ---------------------------------------------------------------- numbers

  var priceEl = null;
  var totalsBox = null;          // the section's own running total, used as the data source
  var priceMirrors = [];         // show the plain variant price
  var totalMirrors = [];         // show the price the customer will actually pay

  function hasAddons() {
    return !!(totalsBox && !totalsBox.hidden);
  }

  function addonsText() {
    if (!hasAddons()) return '';
    return text(totalsBox.querySelector('[data-kbf-addons-total]'));
  }

  function totalText() {
    if (!hasAddons()) return text(priceEl);
    return text(totalsBox.querySelector('[data-kbf-grand-total]')) || text(priceEl);
  }

  function write(el, value) {
    if (!el || !value || el.textContent === value) return;
    el.textContent = value;
    el.classList.remove('kbf-price-flash');
    void el.offsetWidth;                          // restart the highlight
    el.classList.add('kbf-price-flash');
  }

  // ---------------------------------------------------------------- pieces

  /* 1. Inline price — only where there is something to choose. A product with a single
        variant has no picker box, and no price that can change. */
  function buildInlinePrice() {
    var box = document.querySelector('[data-kbf-variants]');
    if (!box || box.querySelector('.kbf-live-price')) return;

    var row = document.createElement('div');
    row.className = 'kbf-live-price';
    row.setAttribute('aria-live', 'polite');

    var label = document.createElement('span');
    label.className = 'kbf-live-price__label';
    label.textContent = 'מחיר';

    var value = document.createElement('span');
    value.className = 'kbf-live-price__value';
    value.textContent = text(priceEl);

    row.appendChild(label);
    row.appendChild(value);
    box.insertBefore(row, box.firstChild);
    priceMirrors.push(value);
  }

  /* 2. Summary card */
  var summary = null, summaryPriceRow = null, summaryAddonsRow = null;
  var stickyNote = null;
  var summaryAddonsValue = null, summaryTotalRow = null;

  function summaryRow(modifier) {
    var row = document.createElement('div');
    row.className = 'kbf-price-summary__row' + (modifier ? ' ' + modifier : '');
    var label = document.createElement('span');
    var value = document.createElement('span');
    value.className = 'kbf-price-summary__value';
    row.appendChild(label);
    row.appendChild(value);
    summary.appendChild(row);
    return { row: row, label: label, value: value };
  }

  function buildSummary() {
    if (!document.querySelector('.kbf-prod__gallery') && !document.querySelector('.kbf-prod__actions')) return;

    summary = document.createElement('div');
    summary.className = 'kbf-price-summary';
    summary.setAttribute('aria-live', 'polite');

    var base = summaryRow();
    base.label.textContent = 'מחיר המוצר';
    summaryPriceRow = base.row;
    priceMirrors.push(base.value);

    var add = summaryRow();
    add.label.textContent = 'תוספות';
    summaryAddonsRow = add.row;
    summaryAddonsValue = add.value;

    var sum = summaryRow('kbf-price-summary__row--sum');
    sum.label.textContent = 'סה"כ';
    summaryTotalRow = sum.row;
    totalMirrors.push(sum.value);

    // Tell the stylesheet it can retire the section's own totals box
    document.documentElement.classList.add('kbf-has-summary');
    placeSummary();
  }

  /* Under the gallery on a phone, where it reads as part of the product.
     On desktop the gallery is a separate column from the form, so the total belongs
     next to the button the customer is about to press. */
  function placeSummary() {
    if (!summary) return;
    var gallery = document.querySelector('.kbf-prod__gallery');
    var actions = document.querySelector('.kbf-prod__actions');

    if (isMobile() && gallery) {
      var video = gallery.querySelector('.kbf-prod__video');
      if (video) gallery.insertBefore(summary, video);
      else gallery.appendChild(summary);
    } else if (actions && actions.parentNode) {
      actions.parentNode.insertBefore(summary, actions);
    } else if (gallery) {
      gallery.appendChild(summary);
    }
  }

  /* 3. Thumbnails scroll sideways now, so the active one can sit outside the visible
        strip after the arrows step past it. Follow whatever the section marks active. */
  function followActiveThumb() {
    var strip = document.querySelector('.kbf-prod__thumbs');
    if (!strip) return;
    var queued = false;

    function reveal() {
      queued = false;
      var active = strip.querySelector('.kbf-prod__thumb.is-active');
      if (!active) return;

      var s = strip.getBoundingClientRect();
      var viewH = window.innerHeight || document.documentElement.clientHeight;

      // Only ever scroll the strip while the customer is actually looking at it. A variant
      // change also re-marks the active thumb, and yanking the page back up to a gallery
      // that is off screen would be worse than leaving the strip where it is.
      if (s.top < 0 || s.bottom > viewH) return;

      var a = active.getBoundingClientRect();
      if (a.left >= s.left - 1 && a.right <= s.right + 1) return;   // already fully in view

      // scrollIntoView rather than scrollLeft arithmetic: this strip is RTL, and the
      // meaning of scrollLeft in RTL differs between engines (this one clamps the
      // negative range to 0). Letting the browser do the maths is the only portable way.
      // The jump is deliberately instant — the stylesheet leaves scroll-behavior alone —
      // so re-centring always lands, animation frames or not.
      active.scrollIntoView({ inline: 'center', block: 'nearest' });
    }

    // A timeout rather than requestAnimationFrame: the section re-marks the active thumb
    // by clearing the class on all 38 and setting it on one, so this has to run once the
    // dust settles — and rAF never fires at all in a tab that isn't being rendered.
    function schedule() {
      if (queued) return;
      queued = true;
      window.setTimeout(reveal, 0);
    }

    if ('MutationObserver' in window) {
      new MutationObserver(schedule).observe(strip, {
        subtree: true, attributes: true, attributeFilter: ['class']
      });
    }
  }

  /* 4. Sticky bar */

  /* The bar and the WhatsApp button both live at the bottom of the screen. The class and
     the measured height let the stylesheet lift the button clear while the bar is up. */
  function showBar(bar, show) {
    bar.classList.toggle('is-visible', show);
    document.documentElement.classList.toggle('kbf-bar-visible', show);
    if (show) {
      document.documentElement.style.setProperty(
        '--kbf-bar-h', Math.round(bar.getBoundingClientRect().height) + 'px'
      );
    }
  }

  function buildStickyBar() {
    var origBtn = findATC();
    if (!origBtn) return null;

    var bar = document.createElement('div');
    bar.className = 'kbf-sticky-atc';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'הוספה לעגלה');

    var info = document.createElement('div');
    info.className = 'kbf-sticky-atc__info';

    var title = document.createElement('span');
    title.className = 'kbf-sticky-atc__title';
    title.textContent = getProductTitle();

    var price = document.createElement('span');
    price.className = 'kbf-sticky-atc__price';
    price.textContent = totalText();
    totalMirrors.push(price);

    // Says why the bar's number is bigger than the item's own price
    stickyNote = document.createElement('span');
    stickyNote.className = 'kbf-sticky-atc__note';
    stickyNote.hidden = true;

    var priceRow = document.createElement('span');
    priceRow.className = 'kbf-sticky-atc__pricerow';
    priceRow.appendChild(price);
    priceRow.appendChild(stickyNote);

    info.appendChild(title);
    info.appendChild(priceRow);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kbf-sticky-atc__btn';
    btn.textContent = atcLabel(origBtn);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var current = findATC();
      if (current) {
        try { current.click(); } catch (err) { /* noop */ }
      }
    });

    var inner = document.createElement('div');
    inner.className = 'kbf-sticky-atc__inner';
    inner.appendChild(info);
    inner.appendChild(btn);

    bar.appendChild(inner);
    document.body.appendChild(bar);
    return { bar: bar, btn: btn, origBtn: origBtn };
  }

  // ---------------------------------------------------------------- refresh

  function refresh() {
    var price = text(priceEl);
    var addons = addonsText();
    var total = totalText();

    priceMirrors.forEach(function (el) { write(el, price); });
    totalMirrors.forEach(function (el) { write(el, total); });

    if (!summary) {
      if (stickyNote) stickyNote.hidden = !addons;
      return;
    }
    // With no paid extras the breakdown is just the price twice over, so only the total shows.
    var breakdown = !!addons;
    summaryPriceRow.hidden = !breakdown;
    summaryAddonsRow.hidden = !breakdown;
    if (breakdown) write(summaryAddonsValue, addons);
    if (stickyNote) {
      stickyNote.hidden = !breakdown;
      if (breakdown) stickyNote.textContent = 'כולל תוספות ' + addons;
    }
    summaryTotalRow.classList.toggle('has-breakdown', breakdown);
  }

  function init() {
    priceEl = findPriceEl();
    totalsBox = document.querySelector('[data-kbf-totals]');
    injectStyles();

    buildInlinePrice();
    buildSummary();
    followActiveThumb();

    var built = buildStickyBar();

    refresh();

    if ('MutationObserver' in window) {
      // The section rewrites the price on every variant change…
      if (priceEl) {
        new MutationObserver(refresh).observe(priceEl, {
          childList: true, characterData: true, subtree: true
        });
      }
      // …and rewrites (and shows/hides) its totals box on every add-on change.
      if (totalsBox) {
        new MutationObserver(refresh).observe(totalsBox, {
          childList: true, characterData: true, subtree: true,
          attributes: true, attributeFilter: ['hidden']
        });
      }
    }

    // Keep the summary on the correct side of the breakpoint
    if (window.matchMedia) {
      var mq = window.matchMedia('(max-width: ' + (MOBILE_MAX - 1) + 'px)');
      var onBreakpoint = function () { placeSummary(); };
      if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);
      else if (mq.addListener) mq.addListener(onBreakpoint);
    }

    if (!built) return;

    // Hide the bar while the real ATC button is on screen
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          showBar(built.bar, !entry.isIntersecting);
        });
      }, { threshold: 0.1 });
      io.observe(built.origBtn);
    } else {
      showBar(built.bar, true);
    }

    // Keep the mirrored button in step with the real one (label + sold-out state)
    var sync = function () {
      var current = findATC();
      if (!current) return;
      built.btn.textContent = atcLabel(current);
      if (current.disabled) built.btn.setAttribute('disabled', 'disabled');
      else built.btn.removeAttribute('disabled');
    };
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(built.origBtn, {
        attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true
      });
    } else {
      setInterval(sync, 1500);
    }
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
